import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { Calendar, Clock, MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Doctors = () => {
  const { language } = useLanguage();
  const [doctors, setDoctors] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [doctorsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/doctors`),
        axios.get(`${API}/settings`),
      ]);
      setDoctors(doctorsRes.data);
      setSettings(settingsRes.data);
    } catch {
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCheckAvailability = (doctorName) => {
    const phone = settings?.doctors_availability_whatsapp || '9876543210';
    const namePart = doctorName ? ` ${doctorName}` : '';
    const message = language === 'en'
      ? `${settings?.doctors_availability_message_en || 'I want to know the availability of Dr.'}${namePart}`
      : `${settings?.doctors_availability_message_hi || 'मैं डॉ. की उपलब्धता जानना चाहता हूं'}${namePart}`;
    window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white font-body" data-testid="doctors-page">
      <Navbar />

      {/* Hero Banner */}
      <PageHero
        kicker={language === 'en' ? 'Our team' : 'हमारी टीम'}
        title={language === 'en' ? 'Our Doctors' : 'हमारे डॉक्टर'}
        subtitle={language === 'en'
          ? 'Meet our team of experienced and dedicated healthcare professionals'
          : 'अनुभवी और समर्पित स्वास्थ्य पेशेवरों की हमारी टीम से मिलें'}
        testId="doctors-hero"
      />

      {loading || doctors === null ? (
        <div className="py-24 text-center text-neutral-400 font-body" data-testid="doctors-loading">Loading...</div>
      ) : doctors.length === 0 ? (
        <div className="py-24 text-center text-neutral-400 font-body" data-testid="doctors-empty">
          {language === 'en' ? 'No doctors available right now.' : 'अभी कोई डॉक्टर उपलब्ध नहीं है।'}
        </div>
      ) : (
        <div data-testid="doctors-rows">
          {doctors.map((doctor, index) => {
            const isEven = index % 2 === 0;
            const isDark = !isEven;
            const name = language === 'en' ? doctor.name_en : doctor.name_hi;
            const specialization = language === 'en' ? doctor.specialization_en : doctor.specialization_hi;
            const qualification = language === 'en' ? doctor.qualification_en : doctor.qualification_hi;
            const experience = language === 'en' ? doctor.experience_en : doctor.experience_hi;
            const days = language === 'en'
              ? (doctor.working_days_en || 'Mon - Sat')
              : (doctor.working_days_hi || 'सोम - शनि');
            const time = language === 'en'
              ? (doctor.working_time_en || '10:00 AM - 5:00 PM')
              : (doctor.working_time_hi || '10:00 AM - 5:00 PM');
            return (
              <Reveal
                key={doctor.id}
                className={isDark ? 'bg-brand-navy text-white' : 'bg-white text-brand-navy'}
                data-testid={`doctor-row-${index}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* Image */}
                  <div className={`relative h-72 sm:h-96 md:h-[480px] lg:h-[560px] overflow-hidden ${!isEven ? 'md:order-2' : ''}`}>
                    {doctor.image_url ? (
                      <img
                        src={doctor.image_url}
                        alt={name}
                        className="absolute inset-0 w-full h-full object-cover grayscale-[0.15] hover:grayscale-0 transition-all duration-700"
                        loading="lazy"
                      />
                    ) : (
                      <div className={`absolute inset-0 flex items-center justify-center font-heading font-light text-6xl tracking-wide ${
                        isDark ? 'bg-brand-navy-dark text-white/30' : 'bg-neutral-100 text-neutral-300'
                      }`}>
                        {(name || '?')[0]}
                      </div>
                    )}
                  </div>

                  {/* Text */}
                  <div className={`flex items-center md:h-[480px] lg:h-[560px] ${!isEven ? 'md:order-1' : ''}`}>
                    <div className="px-8 sm:px-12 md:px-14 lg:px-20 py-14 md:py-12 w-full">
                      <p className={`text-xs uppercase tracking-ultra-wide font-body mb-4 ${
                        isDark ? 'text-brand-teal-light' : 'text-brand-teal'
                      }`}>
                        {specialization}
                      </p>
                      <h3 className="font-heading font-light text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wide mb-6 leading-tight">
                        {name}
                      </h3>
                      {qualification && (
                        <p className={`font-body font-light text-sm mb-3 ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
                          {qualification}
                        </p>
                      )}
                      {experience && (
                        <p className={`font-body font-light text-sm mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
                          {experience}
                        </p>
                      )}

                      {/* Availability strip */}
                      <div className={`border-t pt-5 mb-8 space-y-2 ${isDark ? 'border-white/15' : 'border-neutral-200'}`}>
                        <div className={`flex items-center gap-3 text-sm font-body font-light ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
                          <Calendar className={`h-3.5 w-3.5 ${isDark ? 'text-brand-teal-light' : 'text-brand-teal'}`} />
                          <span>{days}</span>
                        </div>
                        <div className={`flex items-center gap-3 text-sm font-body font-light ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
                          <Clock className={`h-3.5 w-3.5 ${isDark ? 'text-brand-teal-light' : 'text-brand-teal'}`} />
                          <span>{time}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleCheckAvailability(name)}
                        className={`inline-flex items-center gap-2 px-8 py-3 uppercase tracking-ultra-wide text-xs font-body transition-colors ${
                          isDark
                            ? 'bg-white text-brand-navy hover:bg-brand-teal-light'
                            : 'bg-brand-teal text-white hover:bg-brand-teal-dark'
                        }`}
                        data-testid={`doctor-availability-btn-${index}`}
                      >
                        <MessageCircle className="h-4 w-4" />
                        {language === 'en' ? 'Check Availability' : 'उपलब्धता जांचें'}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      <div className="bg-brand-teal-tint py-16 md:py-20" data-testid="doctors-disclaimer">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-heading font-light text-lg uppercase tracking-ultra-wide text-brand-navy mb-4">
            {language === 'en' ? 'Important Notice' : 'महत्वपूर्ण सूचना'}
          </h3>
          <div className="w-12 h-px bg-brand-teal mx-auto mb-6"></div>
          <p className="text-neutral-600 text-sm font-body font-light leading-relaxed">
            {language === 'en'
              ? (settings?.doctors_disclaimer_en || 'The given days and timings for OPD are tentative and only for reference. They are subject to variation, so please confirm the availability of your Doctor.')
              : (settings?.doctors_disclaimer_hi || 'ओपीडी के लिए दिए गए दिन और समय अस्थायी हैं और केवल संदर्भ के लिए हैं।')}
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Doctors;
